import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import DevSecOpsTabs from '../../components/devsecops/DevSecOpsTabs';
import { useNavigate } from 'react-router-dom';
import DevSecOpsComponentCard, { DevSecOpsComponentDto } from '../../components/devsecops/DevSecOpsComponentCard';
import api, { ApiResponse, authService, User } from '../../services/api';
import { devsecopsApi } from '../../services/devsecopsApi';

const DevSecOpsDeliverables: React.FC = () => {
  const navigate = useNavigate();
  const [components, setComponents] = useState<DevSecOpsComponentDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<User['role'] | undefined>(undefined);

  const handleBack = () => {
    navigate('/devsecops');
  };

  const managementArtifacts = [
    {
      title: 'Task Order Management Plan (TOMP)',
      description: 'Comprehensive plan outlining management approach, organizational structure, and communication protocols',
      frequency: 'Initial + Updates as needed'
    },
    {
      title: 'Quality Control Plan (QCP)',
      description: 'Detailed quality assurance processes, testing strategies, and acceptance criteria',
      frequency: 'Initial + Quarterly updates'
    },
    {
      title: 'Transition-In Plan',
      description: 'Knowledge transfer, team onboarding, and operational handoff strategy',
      frequency: 'Initial (30 days)'
    },
    {
      title: 'Sprint Reports',
      description: 'Bi-weekly sprint summaries including completed work, blockers, and upcoming priorities',
      frequency: 'Bi-weekly'
    },
    {
      title: 'Monthly Status Reports',
      description: 'Comprehensive monthly progress reports with metrics, risks, and accomplishments',
      frequency: 'Monthly'
    },
    {
      title: 'Risk Management Plan',
      description: 'Identification, assessment, and mitigation strategies for project risks',
      frequency: 'Initial + Monthly updates'
    }
  ];

  const deliverableSchedule = [
    { deliverable: 'Task Order Management Plan', due: 'TO Start + 15 days', type: 'Management' },
    { deliverable: 'Quality Control Plan', due: 'TO Start + 15 days', type: 'Quality' },
    { deliverable: 'Transition-In Plan', due: 'TO Start + 10 days', type: 'Transition' },
    { deliverable: 'Sprint Reports', due: 'Bi-weekly', type: 'Recurring' },
    { deliverable: 'Monthly Status Reports', due: 'Monthly (5th business day)', type: 'Recurring' },
    { deliverable: 'Quarterly Performance Review', due: 'Quarterly', type: 'Assessment' },
    { deliverable: 'Security Compliance Reports', due: 'Monthly', type: 'Security' },
    { deliverable: 'CI/CD Pipeline Documentation', due: 'TO Start + 30 days', type: 'Technical' },
    { deliverable: 'Transition-Out Plan', due: 'TO End - 90 days', type: 'Transition' }
  ];

  const acceptanceCriteria = [
    'All deliverables must meet Section 508 accessibility standards',
    'Technical documentation must be complete, accurate, and up-to-date',
    'Code must pass all automated tests and security scans',
    'Sprint deliverables must meet Definition of Done criteria',
    'Management artifacts must be submitted on time and in approved formats',
    'All work products must be reviewed and approved by COR/ACOR'
  ];

  const getTypeColor = (type: string) => {
    const colors: { [key: string]: "primary" | "secondary" | "success" | "error" | "warning" | "info" } = {
      'Management': 'primary',
      'Quality': 'success',
      'Transition': 'info',
      'Recurring': 'secondary',
      'Assessment': 'warning',
      'Security': 'error',
      'Technical': 'info'
    };
    return colors[type] || 'default';
  };

  useEffect(() => {
    const fetchComponents = async () => {
      try {
        setLoading(true);
        const response = await devsecopsApi.get<ApiResponse<DevSecOpsComponentDto[]>>(
          '/devsecops/components',
          {
            params: { page: 'deliverables' }
          }
        );

        const data = response.data.data || [];
        setComponents(data);
        setError(null);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Unable to load deliverables components';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchComponents();
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const user = await authService.getCurrentUser();
        setCurrentUserRole(user.role);
      } catch (_err) {
        // unauthenticated users will see read-only cards
      }
    };

    loadUser();
  }, []);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to DevSecOps Program"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            Deliverables & Schedules
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Task Order Management Artifacts, Schedules, and Acceptance Criteria
          </Typography>
        </Box>
      </Box>

      {/* DevSecOps Tabs */}
      <DevSecOpsTabs activeTab="deliverables" />

      {/* Deliverables & Schedules Capabilities (dynamic) */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mb: 3 }}>
        Deliverables & Schedules Capabilities
      </Typography>

      {loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading deliverables components...
        </Typography>
      )}

      {error && (
        <Typography variant="body2" color="error" role="alert" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {components.map((component: DevSecOpsComponentDto) => (
          <Grid item xs={12} md={6} key={component.key}>
            <DevSecOpsComponentCard
              component={component}
              currentUserRole={currentUserRole}
              onUpdated={(updated) => {
                setComponents((prev: DevSecOpsComponentDto[]) =>
                  prev.map((c: DevSecOpsComponentDto) => (c.key === updated.key ? updated : c))
                );
              }}
            />
          </Grid>
        ))}
      </Grid>

      {/* TO Management Artifacts */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <AssignmentIcon sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />
          <Typography variant="h5" color="primary" fontWeight="bold">
            Task Order Management Artifacts
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {managementArtifacts.map((artifact, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card elevation={1} sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <DescriptionIcon sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                      {artifact.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {artifact.description}
                  </Typography>
                  <Chip
                    icon={<ScheduleIcon />}
                    label={artifact.frequency}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        </CardContent>
      </Card>

      {/* Deliverables Schedule */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <TimelineIcon sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />
          <Typography variant="h5" color="primary" fontWeight="bold">
            Deliverables Schedule
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Deliverable</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Due Date</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deliverableSchedule.map((item, index) => (
                <TableRow key={index} hover>
                  <TableCell>{item.deliverable}</TableCell>
                  <TableCell>{item.due}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.type}
                      size="small"
                      color={getTypeColor(item.type)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </CardContent>
      </Card>

      {/* Inspection & Acceptance */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <AssessmentIcon sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />
          <Typography variant="h5" color="primary" fontWeight="bold">
            Inspection & Acceptance Criteria
          </Typography>
        </Box>
        <Typography variant="body1" paragraph>
          All deliverables are subject to Government inspection and acceptance. The following criteria apply:
        </Typography>
        <List>
          {acceptanceCriteria.map((criterion, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText primary={criterion} />
            </ListItem>
          ))}
        </List>
        <Divider sx={{ my: 3 }} />
        <Typography variant="body2" color="text.secondary">
          <strong>Note:</strong> The Contracting Officer's Representative (COR) or Alternate COR (ACOR) will review
          and approve all deliverables within 10 business days of submission. Rejected deliverables must be corrected
          and resubmitted within 5 business days.
        </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default DevSecOpsDeliverables;
